const SVG_NS = 'http://www.w3.org/2000/svg'

type IconBuilder = () => SVGSVGElement

const createSvg = (viewBox = '0 0 24 24') => {
  const svg = document.createElementNS(SVG_NS, 'svg')
  svg.setAttribute('viewBox', viewBox)
  svg.setAttribute('fill', 'none')
  svg.setAttribute('stroke', 'currentColor')
  svg.setAttribute('stroke-width', '1.8')
  svg.setAttribute('stroke-linecap', 'round')
  svg.setAttribute('stroke-linejoin', 'round')
  svg.setAttribute('aria-hidden', 'true')
  return svg
}

const appendPath = (svg: SVGSVGElement, d: string) => {
  const path = document.createElementNS(SVG_NS, 'path')
  path.setAttribute('d', d)
  svg.appendChild(path)
}

const appendCircle = (svg: SVGSVGElement, cx: number, cy: number, r: number) => {
  const circle = document.createElementNS(SVG_NS, 'circle')
  circle.setAttribute('cx', String(cx))
  circle.setAttribute('cy', String(cy))
  circle.setAttribute('r', String(r))
  svg.appendChild(circle)
}

const appendRect = (svg: SVGSVGElement, x: number, y: number, width: number, height: number, rx = 0) => {
  const rect = document.createElementNS(SVG_NS, 'rect')
  rect.setAttribute('x', String(x))
  rect.setAttribute('y', String(y))
  rect.setAttribute('width', String(width))
  rect.setAttribute('height', String(height))
  if (rx) rect.setAttribute('rx', String(rx))
  svg.appendChild(rect)
}

const appendLine = (svg: SVGSVGElement, x1: number, y1: number, x2: number, y2: number) => {
  const line = document.createElementNS(SVG_NS, 'line')
  line.setAttribute('x1', String(x1))
  line.setAttribute('y1', String(y1))
  line.setAttribute('x2', String(x2))
  line.setAttribute('y2', String(y2))
  svg.appendChild(line)
}

const appendPolyline = (svg: SVGSVGElement, points: string) => {
  const polyline = document.createElementNS(SVG_NS, 'polyline')
  polyline.setAttribute('points', points)
  svg.appendChild(polyline)
}

const ICON_BUILDERS: Record<string, IconBuilder> = {
  arrow_forward: () => {
    const svg = createSvg()
    appendLine(svg, 5, 12, 19, 12)
    appendPolyline(svg, '13 6 19 12 13 18')
    return svg
  },
  bar_chart: () => {
    const svg = createSvg()
    appendLine(svg, 4, 20, 20, 20)
    appendRect(svg, 6, 11, 2.5, 7, 1)
    appendRect(svg, 10.75, 7, 2.5, 11, 1)
    appendRect(svg, 15.5, 4, 2.5, 14, 1)
    return svg
  },
  calendar_today: () => {
    const svg = createSvg()
    appendRect(svg, 4, 5, 16, 15, 2)
    appendLine(svg, 8, 3.5, 8, 7)
    appendLine(svg, 16, 3.5, 16, 7)
    appendLine(svg, 4, 9, 20, 9)
    appendRect(svg, 7, 12, 3, 3, 0.8)
    return svg
  },
  call: () => {
    const svg = createSvg()
    appendPath(svg, 'M5.5 4.5c.6-.6 1.55-.66 2.22-.13l2.17 1.74c.71.57.88 1.58.41 2.36l-1.01 1.68a1.5 1.5 0 0 0 .2 1.82l2.55 2.55a1.5 1.5 0 0 0 1.82.2l1.68-1.01c.78-.47 1.79-.3 2.36.41l1.74 2.17c.53.67.47 1.62-.13 2.22l-1.11 1.11c-1.08 1.08-2.69 1.48-4.16.94-2.3-.84-4.73-2.54-7.01-4.82s-3.98-4.71-4.82-7.01c-.54-1.47-.14-3.08.94-4.16z')
    return svg
  },
  category: () => {
    const svg = createSvg()
    appendRect(svg, 4, 4, 6.5, 6.5, 1.5)
    appendRect(svg, 13.5, 4, 6.5, 6.5, 1.5)
    appendRect(svg, 4, 13.5, 6.5, 6.5, 1.5)
    appendRect(svg, 13.5, 13.5, 6.5, 6.5, 1.5)
    return svg
  },
  check_circle: () => {
    const svg = createSvg()
    appendCircle(svg, 12, 12, 8.5)
    appendPolyline(svg, '8.5 12.5 11 15 16 9.5')
    return svg
  },
  chevron_left: () => {
    const svg = createSvg()
    appendPolyline(svg, '14.5 6 8.5 12 14.5 18')
    return svg
  },
  chevron_right: () => {
    const svg = createSvg()
    appendPolyline(svg, '9.5 6 15.5 12 9.5 18')
    return svg
  },
  close: () => {
    const svg = createSvg()
    appendLine(svg, 6, 6, 18, 18)
    appendLine(svg, 18, 6, 6, 18)
    return svg
  },
  download: () => {
    const svg = createSvg()
    appendLine(svg, 12, 4, 12, 15)
    appendPolyline(svg, '7.5 10.5 12 15 16.5 10.5')
    appendPath(svg, 'M5 19h14')
    return svg
  },
  expand_more: () => {
    const svg = createSvg()
    appendPolyline(svg, '6 9 12 15 18 9')
    return svg
  },
  factory: () => {
    const svg = createSvg()
    appendPath(svg, 'M3.5 20V8.5l6 3V8l6 3V4.5l5 2.5V20z')
    appendLine(svg, 8, 20, 8, 16)
    appendLine(svg, 15.5, 20, 15.5, 16)
    return svg
  },
  grid_view: () => {
    const svg = createSvg()
    appendRect(svg, 4, 4, 6, 6, 1)
    appendRect(svg, 14, 4, 6, 6, 1)
    appendRect(svg, 4, 14, 6, 6, 1)
    appendRect(svg, 14, 14, 6, 6, 1)
    return svg
  },
  headset_mic: () => {
    const svg = createSvg()
    appendPath(svg, 'M5 12a7 7 0 0 1 14 0v4')
    appendRect(svg, 4, 12, 3, 5, 1.2)
    appendRect(svg, 17, 12, 3, 5, 1.2)
    appendPath(svg, 'M17 18a3 3 0 0 1-3 3h-2')
    appendCircle(svg, 11, 21, 0.8)
    return svg
  },
  language: () => {
    const svg = createSvg()
    appendCircle(svg, 12, 12, 8.5)
    appendPath(svg, 'M3.8 12h16.4')
    appendPath(svg, 'M12 3.8c2.4 2.2 3.8 5.1 3.8 8.2s-1.4 6-3.8 8.2c-2.4-2.2-3.8-5.1-3.8-8.2S9.6 6 12 3.8z')
    return svg
  },
  list: () => {
    const svg = createSvg()
    appendLine(svg, 9, 7, 19, 7)
    appendLine(svg, 9, 12, 19, 12)
    appendLine(svg, 9, 17, 19, 17)
    appendCircle(svg, 5.5, 7, 0.8)
    appendCircle(svg, 5.5, 12, 0.8)
    appendCircle(svg, 5.5, 17, 0.8)
    return svg
  },
  location_on: () => {
    const svg = createSvg()
    appendPath(svg, 'M12 20s6-4.8 6-10a6 6 0 1 0-12 0c0 5.2 6 10 6 10z')
    appendCircle(svg, 12, 10, 2)
    return svg
  },
  mail: () => {
    const svg = createSvg()
    appendRect(svg, 4, 6, 16, 12, 2)
    appendPolyline(svg, '5.5 7.5 12 13 18.5 7.5')
    return svg
  },
  menu: () => {
    const svg = createSvg()
    appendLine(svg, 4, 7, 20, 7)
    appendLine(svg, 4, 12, 20, 12)
    appendLine(svg, 4, 17, 20, 17)
    return svg
  },
  open_in_new: () => {
    const svg = createSvg()
    appendPath(svg, 'M14 5h5v5')
    appendPath(svg, 'M10 14 19 5')
    appendRect(svg, 5, 9, 10, 10, 2)
    return svg
  },
  request_quote: () => {
    const svg = createSvg()
    appendRect(svg, 5, 4, 12, 16, 2)
    appendPath(svg, 'M9 9h6M9 13h4')
    appendCircle(svg, 18.5, 16.5, 2)
    appendLine(svg, 20, 18, 21, 19)
    return svg
  },
  schedule: () => {
    const svg = createSvg()
    appendCircle(svg, 12, 12, 8.5)
    appendLine(svg, 12, 7.5, 12, 12)
    appendLine(svg, 12, 12, 15.5, 14)
    return svg
  },
  search: () => {
    const svg = createSvg()
    appendCircle(svg, 11, 11, 5.5)
    appendLine(svg, 15.2, 15.2, 19.5, 19.5)
    return svg
  },
  send: () => {
    const svg = createSvg()
    appendPath(svg, 'M4 11.5 20 4l-5.3 16-2.8-5.9z')
    appendPath(svg, 'M20 4 11.9 12.1')
    return svg
  },
  settings_input_component: () => {
    const svg = createSvg()
    appendRect(svg, 5, 6, 14, 12, 2)
    appendLine(svg, 8, 10, 8, 14)
    appendLine(svg, 12, 8.5, 12, 15.5)
    appendLine(svg, 16, 10, 16, 14)
    return svg
  },
  shopping_cart: () => {
    const svg = createSvg()
    appendCircle(svg, 9, 19, 1.3)
    appendCircle(svg, 17, 19, 1.3)
    appendPath(svg, 'M4 5h2l2.2 9.2a1 1 0 0 0 1 .8h8.7a1 1 0 0 0 1-.78L20 8H7.2')
    return svg
  },
  verified: () => {
    const svg = createSvg()
    appendPath(svg, 'M12 3.8 14.5 5l3-.2 1 2.8 2.2 2-.8 2.9.8 2.9-2.2 2-1 2.8-3-.2-2.5 1.2-2.5-1.2-3 .2-1-2.8-2.2-2 .8-2.9-.8-2.9 2.2-2 1-2.8 3 .2z')
    appendPolyline(svg, '9 12.5 11.2 14.7 15.3 10.3')
    return svg
  },
  visibility: () => {
    const svg = createSvg()
    appendPath(svg, 'M2.8 12s3.2-5.5 9.2-5.5S21.2 12 21.2 12s-3.2 5.5-9.2 5.5S2.8 12 2.8 12z')
    appendCircle(svg, 12, 12, 2.5)
    return svg
  },
  airline_seat_recline_extra: () => {
    const svg = createSvg()
    appendCircle(svg, 8, 5.5, 1.8)
    appendPath(svg, 'M8.8 8.5h3.1l2 4.2h3.6')
    appendPath(svg, 'M6.5 9.2 8.2 15h5.3')
    appendPath(svg, 'M4.5 19h14')
    appendPath(svg, 'M13.5 15 16 19')
    return svg
  },
  apps: () => {
    const svg = createSvg()
    ;[4, 10, 16].forEach((x) => {
      ;[4, 10, 16].forEach((y) => appendRect(svg, x, y, 3, 3, 0.7))
    })
    return svg
  },
  award_meal: () => {
    const svg = createSvg()
    appendCircle(svg, 12, 8, 4)
    appendPath(svg, 'M9.5 11.5 8.2 20l3.8-2.4 3.8 2.4-1.3-8.5')
    appendPolyline(svg, '9.8 8 11.3 9.5 14.3 6.5')
    return svg
  },
  construction: () => {
    const svg = createSvg()
    appendPath(svg, 'M4 18 14.5 7.5')
    appendPath(svg, 'M12.5 5.5 18.5 11.5')
    appendPath(svg, 'M14.2 3.8 20.2 9.8')
    appendPath(svg, 'M6.5 15.5 8.5 17.5')
    return svg
  },
  hardware: () => {
    const svg = createSvg()
    appendPath(svg, 'M14.5 5.2a4.4 4.4 0 0 0 4.3 5.8l-8 8a2.4 2.4 0 0 1-3.4-3.4l8-8a4.4 4.4 0 0 1-.9-2.4z')
    appendCircle(svg, 9, 17, 0.9)
    return svg
  },
  manufacturing: () => {
    const svg = createSvg()
    appendPath(svg, 'M12 3.8 14 6l3-.2.7 2.9 2.5 1.6-1.3 2.7 1.3 2.7-2.5 1.6-.7 2.9-3-.2-2 2.2-2-2.2-3 .2-.7-2.9-2.5-1.6L5.1 13 3.8 10.3l2.5-1.6.7-2.9 3 .2z')
    appendCircle(svg, 12, 12, 3.2)
    return svg
  },
  model_training: () => {
    const svg = createSvg()
    appendCircle(svg, 12, 12, 7.5)
    appendCircle(svg, 12, 12, 2.2)
    appendLine(svg, 12, 4.5, 12, 7)
    appendLine(svg, 12, 17, 12, 19.5)
    appendLine(svg, 4.5, 12, 7, 12)
    appendLine(svg, 17, 12, 19.5, 12)
    return svg
  },
  precision_manufacturing: () => {
    const svg = createSvg()
    appendCircle(svg, 12, 12, 3.5)
    appendPath(svg, 'M12 3.5v3M12 17.5v3M3.5 12h3M17.5 12h3M6 6l2.1 2.1M15.9 15.9 18 18M18 6l-2.1 2.1M8.1 15.9 6 18')
    return svg
  },
  sensors: () => {
    const svg = createSvg()
    appendCircle(svg, 12, 12, 2.2)
    appendPath(svg, 'M8.3 8.3a5.2 5.2 0 0 0 0 7.4M15.7 8.3a5.2 5.2 0 0 1 0 7.4')
    appendPath(svg, 'M5.6 5.6a9 9 0 0 0 0 12.8M18.4 5.6a9 9 0 0 1 0 12.8')
    return svg
  },
}

const createGenericIcon = (iconName: string) => {
  const svg = createSvg()
  const seed = [...iconName].reduce((sum, char) => sum + char.charCodeAt(0), 0)
  const variant = seed % 5

  if (variant === 0) {
    appendCircle(svg, 12, 12, 7.5)
    appendPolyline(svg, '8.5 12.5 11 15 16 9.5')
  } else if (variant === 1) {
    appendRect(svg, 5, 5, 14, 14, 3)
    appendPath(svg, 'M8.5 12h7M12 8.5v7')
  } else if (variant === 2) {
    appendPath(svg, 'M12 4 20 8.5v7L12 20 4 15.5v-7z')
    appendPath(svg, 'M12 4v16M4 8.5l8 4.5 8-4.5')
  } else if (variant === 3) {
    appendCircle(svg, 12, 12, 3.2)
    appendPath(svg, 'M12 3.8v3M12 17.2v3M3.8 12h3M17.2 12h3M6.2 6.2l2.1 2.1M15.7 15.7l2.1 2.1M17.8 6.2l-2.1 2.1M8.3 15.7l-2.1 2.1')
  } else {
    appendRect(svg, 4.5, 5, 15, 12, 2)
    appendPath(svg, 'M8 19h8M10 17v2M14 17v2')
  }

  svg.dataset.fallbackFor = iconName
  return svg
}

const getIconBuilder = (iconName: string) => ICON_BUILDERS[iconName] || (() => createGenericIcon(iconName))

const resolveIconName = (el: HTMLElement) => {
  const explicit = el.dataset.iconName?.trim()
  if (explicit) return explicit
  const raw = el.textContent?.trim() || ''
  return raw.replace(/\s+/g, '_')
}

const applyFallbackIcon = (el: HTMLElement) => {
  const iconName = resolveIconName(el)
  if (!iconName) return
  const build = getIconBuilder(iconName)
  if (el.dataset.iconRendered === 'true' && el.dataset.iconName === iconName) return

  const svg = build()
  svg.classList.add('cms-material-symbol-fallback__svg')

  const label = el.getAttribute('aria-label') || iconName.replace(/_/g, ' ')
  el.dataset.iconName = iconName
  el.dataset.iconRendered = 'true'
  el.setAttribute('role', 'img')
  el.setAttribute('aria-label', label)
  el.textContent = ''
  el.appendChild(svg)
}

const walkAndApply = (root: ParentNode) => {
  if (root instanceof HTMLElement && root.classList.contains('material-symbols-outlined')) {
    applyFallbackIcon(root)
  }
  root.querySelectorAll?.('.material-symbols-outlined').forEach((node) => {
    if (node instanceof HTMLElement) applyFallbackIcon(node)
  })
}

export default defineNuxtPlugin(() => {
  if (import.meta.server) return

  const injectStyles = () => {
    if (document.getElementById('cms-material-symbol-fallback-style')) return
    const style = document.createElement('style')
    style.id = 'cms-material-symbol-fallback-style'
    style.textContent = `
.material-symbols-outlined[data-icon-rendered="true"] {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  line-height: 1;
}
.material-symbols-outlined[data-icon-rendered="true"] > .cms-material-symbol-fallback__svg {
  width: 1em;
  height: 1em;
  flex: 0 0 auto;
  overflow: visible;
}
`
    document.head.appendChild(style)
  }

  const start = () => {
    injectStyles()
    walkAndApply(document)

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          const parent = mutation.target.parentElement
          if (parent?.classList.contains('material-symbols-outlined')) applyFallbackIcon(parent)
          return
        }
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement || node instanceof DocumentFragment) {
            walkAndApply(node)
          }
        })
      })
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true })
  } else {
    start()
  }
})